import React, { useState, useEffect } from 'react';
import { Package, Check } from 'lucide-react';
import { ProductVariant } from '../types';

interface WeightSelectorProps {
  variants: ProductVariant[];
  selectedVariant: ProductVariant | null;
  onVariantChange: (variant: ProductVariant) => void;
  className?: string;
}

const WeightSelector: React.FC<WeightSelectorProps> = ({
  variants,
  selectedVariant,
  onVariantChange,
  className = ''
}) => {
  const [hoveredVariant, setHoveredVariant] = useState<string | null>(null);

  // Инициализация варианта теперь происходит в родительском компоненте ProductDetail
  // Это обеспечивает правильную синхронизацию состояния на всех устройствах

  if (!variants || variants.length === 0) {
    return null;
  }

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('ru-RU').format(price);
  };

  return (
    <div className={`${className}`}>
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
        <Package className="w-5 h-5 mr-2 text-primary-600 dark:text-primary-400" />
        Выберите вес упаковки
      </h3>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {variants.map((variant) => (
          <button
            key={variant.id}
            onClick={() => onVariantChange(variant)}
            onMouseEnter={() => setHoveredVariant(variant.id)}
            onMouseLeave={() => setHoveredVariant(null)}
            className={`
              relative group p-4 rounded-xl border-2 transition-all duration-300 transform hover:scale-105 active:scale-95 h-24 flex flex-col justify-center
              ${selectedVariant?.id === variant.id
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 shadow-lg'
                : 'border-gray-200 dark:border-gray-600 hover:border-primary-300 dark:hover:border-primary-400 bg-white dark:bg-gray-800'
              }
              ${!variant.inStock
                ? 'opacity-60 cursor-pointer'
                : 'cursor-pointer hover:shadow-md'
              }
            `}
          >
            {/* Индикатор выбора */}
            {selectedVariant?.id === variant.id && (
              <div className="absolute -top-2 -right-2 w-6 h-6 bg-primary-500 rounded-full flex items-center justify-center">
                <Check className="w-4 h-4 text-white" />
              </div>
            )}

            {/* Статус наличия и вес */}
            <div className="absolute top-2 left-2 right-2 flex justify-between items-start">
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                variant.inStock
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200'
                  : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200'
              }`}>
                {variant.inStock ? 'В наличии' : 'Под заказ'}
              </span>
              
              <span className={`text-lg font-bold ${
                selectedVariant?.id === variant.id
                  ? 'text-primary-600 dark:text-primary-400'
                  : 'text-gray-900 dark:text-white'
              }`}>
                {variant.weight}
              </span>
            </div>

            {/* Описание */}
            {variant.description && (
              <div className="text-center mt-8">
                <span className={`text-sm ${
                  selectedVariant?.id === variant.id
                    ? 'text-primary-600 dark:text-primary-400'
                    : 'text-gray-500 dark:text-gray-400'
                }`}>
                  {variant.description}
                </span>
              </div>
            )}

            {/* Специальная отметка для концентрированного порошка */}
            {variant.isConcentrated && (
              <div className="absolute -bottom-1 -left-1">
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200">
                  Концентрированный
                </span>
              </div>
            )}

            {/* Анимация при наведении */}
            {hoveredVariant === variant.id && (
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary-50/50 dark:via-primary-900/20 to-transparent rounded-xl animate-pulse"></div>
            )}
          </button>
        ))}
      </div>


    </div>
  );
};

export default WeightSelector;
